import { i18n } from "@src/i18n/i18n";
import store from "@src/reducers/store";
import { SupportEmailContext, UserInfo } from "../entities/user-info";

const formatEntries = (
  entries: Array<[string, string | undefined]>,
): string[] =>
  entries
    .filter(([, value]) => Boolean(value?.trim()))
    .map(([label, value]) => `${label}: ${value!.trim()}`);

const formatContextLines = (context: SupportEmailContext): string[] =>
  formatEntries([
    ["Gateway name", context.gatewayName],
    ["Gateway UUID", context.gatewayUuid],
    ["Namespace ID", context.namespaceId],
    ["Namespace name", context.namespaceName],
    ["Machine name", context.machineName],
    ["Machine UUID", context.machineUuid],
    ["Source ID", context.sourceId],
    ["Hostname/IP", context.hostname],
    ["Failure stage", context.failureStage],
    ["Failed skill", context.failedSkill],
    ["Operation ID", context.operationId],
    ["Failure reason", context.failureReason],
    ["Error message", context.errorMessage],
    ...Object.entries(context.errorDetails || {}).map(
      ([label, value]) => [label, value] as [string, string | undefined],
    ),
  ]);

export class OnboardingSupportEmailBuilder {
  private readonly supportEmail = "support@tomorrowthings.com";

  private getLanguage() {
    return store.getState().yourSlice.language;
  }

  private buildMailto(subject: string, lines: string[]): string {
    return `mailto:${this.supportEmail}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(lines.join("\n"))}`;
  }

  buildGatewaySupportEmail(
    userInfo: UserInfo,
    sourceDsn: string,
    connectionId: string | undefined,
    context: SupportEmailContext,
  ): string {
    const language = this.getLanguage();
    const lines = [
      i18n("modules.zendeskContactUrl.greeting", language),
      "",
      i18n(
        "modules.zendeskContactUrl.failedGatewayOnboardingDescription",
        language,
      ),
    ];

    const sourceLines = formatEntries([
      [i18n("modules.zendeskContactUrl.sourceDsnLabel", language), sourceDsn],
      [
        i18n("modules.zendeskContactUrl.connectionIdLabel", language),
        connectionId,
      ],
    ]);

    const gatewayLines = formatEntries([
      ["Gateway name", context.gatewayName],
      ["Gateway UUID", context.gatewayUuid],
      ["Namespace ID", context.namespaceId || userInfo.namespace],
      ["Namespace name", context.namespaceName],
      ["Failure stage", context.failureStage],
      ["Failure reason", context.failureReason],
      ["Operation ID", context.operationId],
      ...Object.entries(context.errorDetails || {}).map(
        ([label, value]) => [label, value] as [string, string | undefined],
      ),
    ]);

    const userLines = formatEntries([
      [i18n("modules.zendeskContactUrl.emailLabel", language), userInfo.email],
      [
        i18n("modules.zendeskContactUrl.namespaceLabel", language),
        userInfo.namespace,
      ],
    ]);

    if (sourceLines.length) lines.push("", ...sourceLines);
    if (gatewayLines.length)
      lines.push("", "Gateway details:", ...gatewayLines);
    if (userLines.length) {
      lines.push(
        "",
        i18n("modules.zendeskContactUrl.userInfoHeader", language) + ":",
        ...userLines,
      );
    }

    return this.buildMailto(
      i18n("modules.zendeskContactUrl.onboardingSupportSubject", language),
      lines,
    );
  }

  buildMachineSupportEmail(
    userInfo: UserInfo,
    context: SupportEmailContext,
  ): string {
    const language = this.getLanguage();
    const userLines = formatEntries([
      [i18n("modules.zendeskContactUrl.emailLabel", language), userInfo.email],
      ["Namespace ID", context.namespaceId || userInfo.namespace],
    ]);
    const detailLines = formatContextLines(context).filter(
      (line) => !line.startsWith("Namespace ID:"),
    );

    const lines = [
      i18n("modules.zendeskContactUrl.greeting", language),
      "",
      i18n(
        "modules.zendeskContactUrl.failedGatewayOnboardingDescription",
        language,
      ),
    ];

    if (userLines.length) {
      lines.push(
        "",
        i18n("modules.zendeskContactUrl.userInfoHeader", language) + ":",
        ...userLines,
      );
    }
    if (detailLines.length) {
      lines.push("", "Onboarding details:", ...detailLines);
    }

    return this.buildMailto(
      i18n("modules.zendeskContactUrl.onboardingSupportSubject", language),
      lines,
    );
  }
}
